import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { formatInTimeZone } from 'date-fns-tz';
import { useDispatch } from 'react-redux';
import ImageCarouselDialog from 'app/views/Argyle/sites/ObservationSection/ImageCarouselDialog';
import { deleteARImage } from 'app/redux/slices/arImageSlice';
import { setSnackbar } from 'app/redux/slices/snackbarSlice';
import { useSite } from 'app/contexts/SiteContext';
import { PCIntegrationIcon } from 'app/components/AgyleComponents/ArgyleIcon/ProCoreIntegrationIcon';
import AuthImg from 'app/components/AuthImg';
import {
  getAugmentedUrl,
  getDeviceType,
  getPhotographer,
  getTimeStampDate,
  getTimeStampMs,
} from './arImageFields';
import { downloadArImagesZip, downloadSingleArImage } from './arImageZip';

const ROWS_PER_PAGE = 12;
const FALLBACK_IMAGE = '/assets/images/argyle/newLogo/Argyle Logo - 01.svg';
const PROCORE_LOGO = '/assets/images/argyle/newLogo/PCOR.svg';
const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatDate = (date) =>
  date ? formatInTimeZone(date, TIME_ZONE, 'MM/dd/yyyy hh:mm a') : 'N/A';

const dateInputToMs = (value, endOfDay = false) => {
  if (!value) return null;
  const ms = new Date(
    `${value}T${endOfDay ? '23:59:59' : '00:00:00'}`,
  ).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const ARImageGalleryPage = ({ images, projectId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { siteInfo } = useSite();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [manageMode, setManageMode] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [photographerFilter, setPhotographerFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [zipProgress, setZipProgress] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const photographerOptions = useMemo(() => {
    const set = new Set();
    images.forEach((img) => {
      const who = getPhotographer(img);
      if (who) set.add(who);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [images]);

  const processedImages = useMemo(() => {
    const fromMs = dateInputToMs(dateFrom);
    const toMs = dateInputToMs(dateTo, true);

    const filtered = images.filter((img) => {
      if (photographerFilter && getPhotographer(img) !== photographerFilter) {
        return false;
      }
      if (fromMs != null || toMs != null) {
        const ts = getTimeStampMs(img);
        if (fromMs != null && ts < fromMs) return false;
        if (toMs != null && ts > toMs) return false;
      }
      return true;
    });

    const dir = order === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let cmp;
      if (orderBy === 'photographer') {
        cmp = getPhotographer(a).localeCompare(getPhotographer(b), undefined, {
          sensitivity: 'base',
        });
      } else {
        cmp = getTimeStampMs(a) - getTimeStampMs(b);
      }
      return cmp * dir;
    });
  }, [images, photographerFilter, dateFrom, dateTo, orderBy, order]);

  const totalPages = Math.ceil(processedImages.length / ROWS_PER_PAGE) || 1;
  const safePage = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * ROWS_PER_PAGE;
    return processedImages
      .slice(start, start + ROWS_PER_PAGE)
      .map((image, localIndex) => ({ image, globalIndex: start + localIndex }));
  }, [processedImages, safePage]);

  const carouselItems = useMemo(
    () => processedImages.map((image) => ({ image })),
    [processedImages],
  );

  const selectedSet = useMemo(
    () => new Set(selectedImageIds),
    [selectedImageIds],
  );
  const allFilteredSelected =
    processedImages.length > 0 &&
    processedImages.every((img) => selectedSet.has(img.id));
  const someFilteredSelected =
    processedImages.some((img) => selectedSet.has(img.id)) &&
    !allFilteredSelected;

  const handleSort = (property) => {
    if (orderBy === property) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(property);
      setOrder(property === 'photographer' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const toggleSelect = useCallback((imageId) => {
    setSelectedImageIds((prev) =>
      prev.includes(imageId)
        ? prev.filter((id) => id !== imageId)
        : [...prev, imageId],
    );
  }, []);

  const handleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(processedImages.map((img) => img.id));
      setSelectedImageIds((prev) => prev.filter((id) => !filteredIds.has(id)));
    } else {
      setSelectedImageIds((prev) =>
        Array.from(new Set([...prev, ...processedImages.map((img) => img.id)])),
      );
    }
  };

  const handleRowActivate = (item) => {
    if (manageMode) {
      toggleSelect(item.image.id);
    } else {
      setCarouselStartIndex(item.globalIndex);
      setCarouselOpen(true);
    }
  };

  const handleSingleDownload = async (e, image) => {
    e.stopPropagation();
    try {
      await downloadSingleArImage(image);
    } catch (err) {
      dispatch(
        setSnackbar({
          status: 'error',
          message: `Download failed: ${err?.message || 'unknown error'}`,
        }),
      );
    }
  };

  const handleBatchDownload = async () => {
    const selected = images.filter((img) => selectedSet.has(img.id));
    if (selected.length === 0) return;

    setZipProgress({ done: 0, total: selected.length });
    try {
      const { success, failed, total } = await downloadArImagesZip({
        images: selected,
        projectName: siteInfo?.siteName || 'project',
        onProgress: (done, t) => setZipProgress({ done, total: t }),
      });

      if (success === 0) {
        dispatch(
          setSnackbar({
            status: 'error',
            message: 'Could not download any of the selected photos.',
          }),
        );
      } else if (failed.length > 0) {
        dispatch(
          setSnackbar({
            status: 'info',
            message: `Downloaded ${success} of ${total} photos (${failed.length} failed).`,
          }),
        );
      } else {
        dispatch(
          setSnackbar({
            status: 'success',
            message: `Downloaded ${success} photo${success === 1 ? '' : 's'} as a zip.`,
          }),
        );
      }
    } catch (err) {
      dispatch(
        setSnackbar({
          status: 'error',
          message: `Zip download failed: ${err?.message || 'unknown error'}`,
        }),
      );
    } finally {
      setZipProgress(null);
    }
  };

  const handleBatchDelete = () => {
    selectedImageIds.forEach((imageId) =>
      dispatch(deleteARImage({ projectId, id: imageId })),
    );
    setSelectedImageIds([]);
    setConfirmDeleteOpen(false);
  };

  const handlePageChange = (_, value) => setPage(value);

  const procoreProjectId = siteInfo?.procoreProjectId;
  const procoreImageCategoryId = siteInfo?.procoreImageCategoryId;
  const canSyncProcore = Boolean(procoreProjectId && procoreImageCategoryId);
  const procoreGalleryLink = `https://procore.com/${procoreProjectId}/project/images/show_album?id=${procoreImageCategoryId}`;

  const zipping = zipProgress != null;

  const renderRow = (item) => {
    const { image } = item;
    const isSelected = selectedSet.has(image.id);
    const date = getTimeStampDate(image);
    const photographer = getPhotographer(image) || '—';
    const device = getDeviceType(image) || '—';

    return (
      <TableRow
        key={image.id}
        hover
        selected={isSelected}
        tabIndex={0}
        role="button"
        sx={{ cursor: 'pointer' }}
        onClick={() => handleRowActivate(item)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleRowActivate(item);
          }
        }}
      >
        {manageMode && (
          <TableCell padding="checkbox">
            <Checkbox
              checked={isSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleSelect(image.id)}
            />
          </TableCell>
        )}
        <TableCell>
          <Box
            sx={{
              width: 96,
              height: 64,
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: 'action.hover',
            }}
          >
            <AuthImg
              src={image.thumbnailUrl}
              fallbackSrc={FALLBACK_IMAGE}
              alt={`AR photo ${image.id}`}
              lazy
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        </TableCell>
        <TableCell>{formatDate(date)}</TableCell>
        <TableCell>{photographer}</TableCell>
        <TableCell>{device}</TableCell>
        <TableCell align="right">
          <Tooltip title="Download photo">
            <span>
              <IconButton
                size="small"
                disabled={!getAugmentedUrl(image)}
                onClick={(e) => handleSingleDownload(e, image)}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box p={3}>
      <Card sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center">
          <PhotoLibraryIcon fontSize="large" />
          <Typography variant="h2" ml={1}>
            AR Photo Gallery
          </Typography>
        </Box>
      </Card>

      <Card sx={{ p: 2, mb: 2 }}>
        {!manageMode ? (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setManageMode(true)}
          >
            Enter Manage Mode
          </Button>
        ) : (
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Button
              variant="contained"
              color="secondary"
              onClick={handleBatchDownload}
              disabled={selectedImageIds.length === 0 || zipping}
              startIcon={
                zipping ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
            >
              {zipping
                ? `Zipping ${zipProgress.done}/${zipProgress.total}…`
                : `Download Selected (${selectedImageIds.length})`}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={selectedImageIds.length === 0 || zipping}
              startIcon={<DeleteIcon />}
            >
              Delete Selected
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={zipping}
              onClick={() => {
                setManageMode(false);
                setSelectedImageIds([]);
              }}
            >
              Cancel Manage Mode
            </Button>
          </Box>
        )}
      </Card>

      {canSyncProcore && (
        <Card elevation={3} sx={{ mx: 'auto', mt: 4, mb: 4, borderRadius: 2 }}>
          <CardHeader
            title={<Typography variant="h6">Procore Image Gallery</Typography>}
            action={<PCIntegrationIcon />}
            sx={{ pb: 0 }}
          />
          <CardContent>
            <Button
              variant="contained"
              color="secondary"
              startIcon={
                <Box
                  component="img"
                  src={PROCORE_LOGO}
                  alt=""
                  sx={{ width: 20, height: 20 }}
                />
              }
              onClick={() => window.open(procoreGalleryLink, '_blank')}
            >
              View Album in Procore
            </Button>
          </CardContent>
        </Card>
      )}

      <Card sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="ar-photographer-filter">Photographer</InputLabel>
            <Select
              labelId="ar-photographer-filter"
              label="Photographer"
              value={photographerFilter}
              onChange={(e) => {
                setPhotographerFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All photographers</MenuItem>
              {photographerOptions.map((email) => (
                <MenuItem key={email} value={email}>
                  {email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="From"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
          />

          {(photographerFilter || dateFrom || dateTo) && (
            <Button
              size="small"
              onClick={() => {
                setPhotographerFilter('');
                setDateFrom('');
                setDateTo('');
                setPage(1);
              }}
              color="secondary"
              variant="contained"
            >
              Clear filters
            </Button>
          )}

          <Box flexGrow={1} />
          <Typography variant="body2" color="text.secondary">
            {processedImages.length} photo
            {processedImages.length === 1 ? '' : 's'}
          </Typography>
        </Box>

        {processedImages.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">
              No AR photos match the current filters.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size={isSmallScreen ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  {manageMode && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allFilteredSelected}
                        indeterminate={someFilteredSelected}
                        onChange={handleSelectAllFiltered}
                        inputProps={{
                          'aria-label': 'Select all filtered photos',
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>Photo</TableCell>
                  <TableCell sortDirection={orderBy === 'date' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'date'}
                      direction={orderBy === 'date' ? order : 'desc'}
                      onClick={() => handleSort('date')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={orderBy === 'photographer' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'photographer'}
                      direction={orderBy === 'photographer' ? order : 'asc'}
                      onClick={() => handleSort('photographer')}
                    >
                      Photographer
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>Device</TableCell>
                  <TableCell align="right">Download</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{pageRows.map(renderRow)}</TableBody>
            </Table>
          </TableContainer>
        )}

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={safePage}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}

        <ImageCarouselDialog
          open={carouselOpen}
          onClose={() => setCarouselOpen(false)}
          images={carouselItems}
          initialIndex={carouselStartIndex}
        />
      </Card>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        aria-labelledby="batch-delete-dialog-title"
      >
        <DialogTitle id="batch-delete-dialog-title">
          Delete Photos
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete{' '}
            {selectedImageIds.length} selected photo
            {selectedImageIds.length === 1 ? '' : 's'}? This cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setConfirmDeleteOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBatchDelete}
            startIcon={<DeleteIcon />}
          >
            Delete {selectedImageIds.length} Photo
            {selectedImageIds.length === 1 ? '' : 's'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ARImageGalleryPage.propTypes = {
  images: PropTypes.array.isRequired,
  projectId: PropTypes.string.isRequired,
};

export default ARImageGalleryPage;
